import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useRef } from 'react';
import { LabeledSection } from './LabeledSection';
import { useUpdateTag } from 'entities/tag';

interface NotesSectionProps {
  tagId: string;
  documentSuperId: number;
  defaultValue: string;
  delay?: number;
  disabled?: boolean;
}

export const NotesSection = ({ tagId, documentSuperId, defaultValue, delay = 500, disabled = false }: NotesSectionProps) => {
  const { t } = useTranslation('tag');

  const { mutate: updateTag } = useUpdateTag();
  const pendingRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const flush = useCallback(() => {
    clearTimeout(timerRef.current);
    if (pendingRef.current !== null) {
      updateTag({ id: tagId, notes: pendingRef.current, documentSuperId });
      pendingRef.current = null;
    }
  }, [tagId, documentSuperId, updateTag]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      pendingRef.current = e.target.value;
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, delay);
    },
    [flush, delay]
  );

  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
      flush();
    };
  }, [flush]);

  return (
    <LabeledSection label={t('notes.title')}>
      <textarea className="notes-textarea" placeholder={t('notes.add')} defaultValue={defaultValue} onChange={handleChange} rows={3} disabled={disabled} />
    </LabeledSection>
  );
};
